import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLine, EASE } from "@/components/motion";

const LENS = [
  { term: "Investment", statement: "Understanding the asset." },
  { term: "Feasibility", statement: "Testing market viability." },
  { term: "Commercial", statement: "Connecting demand and profit." },
  { term: "Operations", statement: "Understanding property performance." },
];

export default function Hero() {
  const [active, setActive] = useState(0);
  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);

  return (
    <section className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7 lg:pt-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="overline-label"
              data-testid="hero-overline"
            >
              Rushikesh Patil — Hospitality Strategy · Investment · Commercial Analytics
            </motion.p>

            <h1 className="mt-8 font-serif font-light leading-[0.98] tracking-tighter text-ink text-5xl sm:text-7xl lg:text-[5.6rem]" data-testid="hero-headline">
              <MaskLine delay={0.45}>From hotel</MaskLine>
              <MaskLine delay={0.58}>operations to</MaskLine>
              <MaskLine delay={0.71} className="italic text-moss">hospitality strategy.</MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
              className="mt-8 max-w-xl font-serif text-xl sm:text-2xl font-light leading-snug text-smoke"
              data-testid="hero-subheadline"
            >
              I explore how hotels perform, compete and create value.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.12, ease: EASE }}
              className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-fog"
            >
              MSc Strategic Hotel Management candidate, graduating in September 2026, combining hotel operations experience with investment, feasibility and commercial analysis.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.24, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/work"
                data-testid="hero-cta-work"
                className="group inline-flex items-center gap-2 bg-moss px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-ink"
              >
                Explore My Work
                <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 border border-ink/25 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-paper"
              >
                About Me
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5" ref={photoRef}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
              className="relative"
            >
              <div className="overflow-hidden rounded-t-[999px] border border-hairline bg-moss">
                <motion.img
                  src="/rushikesh-patil-portrait.webp"
                  alt="Rushikesh Patil — hospitality strategy and commercial analysis"
                  style={{ y }}
                  className="image-treatment aspect-[4/5] w-full scale-110 object-cover object-top"
                  loading="eager"
                  data-testid="hero-portrait"
                />
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease: EASE }}
          className="mt-16 border-t border-hairline pb-20 pt-8 sm:mt-20"
          data-testid="professional-lens"
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <p className="overline-label">A professional lens</p>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-2 lg:col-span-4" role="tablist" aria-label="Professional lens">
              {LENS.map((l, i) => (
                <button
                  key={l.term}
                  role="tab"
                  aria-selected={active === i}
                  data-testid={`lens-${l.term.toLowerCase()}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`border px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                    active === i
                      ? "border-moss bg-moss text-paper"
                      : "border-hairline bg-transparent text-smoke hover:border-moss/50 hover:text-ink"
                  }`}
                >
                  {l.term}
                </button>
              ))}
            </div>
            <div className="lg:col-span-5">
              <AnimatePresence mode="wait">
                <motion.p
                  key={active}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="font-serif text-2xl sm:text-[1.7rem] font-light italic leading-snug text-ink"
                  data-testid="lens-statement"
                >
                  “{LENS[active].statement}”
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
